import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@c2fo/liquidity';
import { GetMeQuery } from '@/generated/gql/graphql';
import { useReporting } from '@/reporting';
import useUserDetails from '@/shared/data/useUserDetails';

// Map the what's new article URL to the language code
const WHATS_NEW_URLS: Record<string, string> = {
  en: 'https://help.c2fo.com/en/articles/13853451-what-s-new-in-c2fo-early-pay-app',
  de: 'https://help.c2fo.com/de/articles/13853451-was-ist-neu-in-der-c2fo-early-pay-app',
  es: 'https://help.c2fo.com/es/articles/13853451-novedades-en-la-aplicacion-c2fo-early-pay',
  fr: 'https://help.c2fo.com/fr/articles/13853451-quoi-de-neuf-dans-l-application-c2fo-early-pay',
  it: 'https://help.c2fo.com/it/articles/13853451-novita-nell-app-c2fo-early-pay',
  ja: 'https://help.c2fo.com/ja/articles/13853451-c2fo-アーリーペイ-アプリの新機能',
  nl: 'https://help.c2fo.com/nl/articles/13853451-wat-is-er-nieuw-in-de-c2fo-early-pay-app',
  'pt-BR': 'https://help.c2fo.com/pt-BR/articles/13853451-novidades-no-aplicativo-c2fo-early-pay',
  ru: 'https://help.c2fo.com/ru/articles/13853451-что-нового-в-приложении-c2fo-early-pay',
  tr: 'https://help.c2fo.com/tr/articles/13853451-c2fo-erken-odeme-uygulamasindaki-yenilikler',
  'zh-CN': 'https://help.c2fo.com/zh-CN/articles/13853451-c2fo-提前付款-early-pay-app-的新功能',
  'zh-TW': 'https://help.c2fo.com/zh-TW/articles/13853451-c2fo-提前付款-early-pay-app-的新功能',
};

type SegmentKey = 'SWEET' | 'SUN';

const ROLLOUT_DATES: Record<SegmentKey, string> = {
  // Low spot accounts already migrated to new homepage
  SWEET: '2026-05-18',
  // Very far future because no plan for sun yet
  SUN: '2029-01-01',
};

const TWO_WEEKS_IN_MS = 14 * 24 * 60 * 60 * 1000;

const UnificationUpdateBanner = () => {
  const { t, i18n } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { data: segment } = useUserDetails(useCallback((user: GetMeQuery) => user.me?.account?.segment, []));

  const whatsNewUrl = WHATS_NEW_URLS[i18n.language] ?? WHATS_NEW_URLS['en'];

  const getSegmentKey = (): SegmentKey => {
    if (segment === 'Sweet') {
      return 'SWEET';
    }

    return 'SUN';
  };

  const handleSeeWhatsNewClick = () => {
    trackEnterpriseEvent('unification-update-banner::clicked', {
      segment,
    });
    window.open(whatsNewUrl, '_blank', 'noreferrer');
  };

  const shouldShowBanner = (): boolean => {
    const segmentKey = getSegmentKey();
    const rolloutDateString = ROLLOUT_DATES[segmentKey];
    const rolloutDate = new Date(rolloutDateString);
    const twoWeeksBeforeRollout = new Date(rolloutDate.getTime() - TWO_WEEKS_IN_MS);
    const now = new Date();

    return now >= twoWeeksBeforeRollout;
  };

  if (!shouldShowBanner()) {
    return null;
  }

  return (
    <div className="mb-6">
      <Alert
        type="info"
        variant="filled"
        title={t('experienceUpgradeBanner.title')}
        description={t('experienceUpgradeBanner.description')}
        action={{
          onClick: handleSeeWhatsNewClick,
          text: t('experienceUpgradeBanner.buttonText'),
        }}
        full
      />
    </div>
  );
};

export default UnificationUpdateBanner;
